import { computed, watch, type Ref } from 'vue';
import { storeToRefs } from 'pinia';
import { provideLocal } from '@vueuse/core';

import { useOutfittingStore } from '@nuxt-common/outfitting/app/stores/useOutfittingStore';

import { useProductQuickviewStore, type PqvPayloadBasicType, type PqvTaggingBasicType } from '~/stores/useProductQuickviewStore';

import { PDP_PRODUCT_QUICKVIEW_REPO, PDP_PRODUCT_QUICKVIEW_TAGGING } from './const';

type PqvPayloadBasicTypeRef = Ref<PqvPayloadBasicType>;

/**
 * Provides product quick view (PQV) related state and functions to descendant components.
 * This composable centralizes the logic for handling PQV payloads from different sources
 * (e.g., global state, outfitting store) and makes it available via Vue's provide/inject mechanism.
 * It is intended to be used in a parent component (like `ProductView.vue`) to set up the context
 * for components that need to interact with the PQV slideout.
 */
export function useProductQuickviewProvide() {
  // Retrieves the global PQV payload for the Product Detail Page (PDP).
  const productQvStore = useProductQuickviewStore();
  const { pdpPqvPayload, pdpPqvOptions, pdpPqvTagging } = storeToRefs(productQvStore);
  const { clearPdpPqvTagging } = productQvStore;
  // Retrieves the PQV payload specifically from the outfitting feature store.
  const { pqvPayload: outfittingPqvPayload }: { pqvPayload: PqvPayloadBasicTypeRef } = storeToRefs(useOutfittingStore());

  /**
   * A computed property that returns `true` if there is an active PQV payload
   * from either the global PDP state or the outfitting store.
   * This is useful for determining whether the PQV component should be rendered.
   */
  const isPqvPayloadActive = computed<boolean>(() => !!(pdpPqvPayload.value || outfittingPqvPayload.value));

  /**
   * Sets up watchers on both the global and outfitting PQV payloads.
   * When either payload changes, the provided callback function is executed.
   * This allows child components to react to payload updates (e.g., to open the slideout).
   * @param cb - A callback function that receives the updated payload.
   */
  const watchPayloadUpdate = (cb: (payload: PqvPayloadBasicType) => void) => {
    [pdpPqvPayload, outfittingPqvPayload].forEach((payload: PqvPayloadBasicTypeRef) => {
      watch(() => payload.value, cb, { immediate: true });
    });
  };

  /**
   * Watches for updates inside the store options and performs a deep payload property copy.
   * This structure cleanly updates the keys inside our stable proxy container.
   * @param tagging - The reactive tagging object for different events.
   */
  const watchTaggingUpdate = (tagging: PqvTaggingBasicType) => {
    watch(
      () => pdpPqvTagging.value,
      (customTagging) => {
        const source = customTagging && Object.keys(customTagging).length ? customTagging : PDP_PRODUCT_QUICKVIEW_TAGGING;

        tagging.onDrawerLoad = { ...(source.onDrawerLoad || {}) };
        tagging.onProductDetailClick = { ...(source.onProductDetailClick || {}) };
        tagging.onAddToBagRequest = { ...(source.onAddToBagRequest || {}) };
      },
      { immediate: true },
    );
  };

  /**
   * Provides the reactive state and watcher function to all descendant components.
   * Components can then inject this functionality using `useProductQuickview()`.
   */
  provideLocal(PDP_PRODUCT_QUICKVIEW_REPO, {
    isPqvPayloadActive,
    pdpPqvOptions,
    pdpPqvTagging,
    clearPdpPqvTagging,
    watchPayloadUpdate,
    watchTaggingUpdate,
  });
}
